import { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Package,
  Search,
  Loader2,
  MapPin,
  CheckCircle2,
  Truck,
  Clock,
  CreditCard,
  ShieldCheck,
  Anchor,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STEPS = [
  { key: 'awaiting_payment', label: 'Awaiting Payment', icon: CreditCard },
  { key: 'payment_verification', label: 'Verifying Payment', icon: ShieldCheck },
  { key: 'payment_confirmed', label: 'Payment Confirmed', icon: CheckCircle2 },
  { key: 'processing', label: 'Processing', icon: Package },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'completed', label: 'Delivered', icon: Anchor },
];

const statusLabels = {
  awaiting_payment: 'Awaiting Payment',
  payment_verification: 'Verifying Payment',
  payment_confirmed: 'Payment Confirmed',
  processing: 'Processing',
  shipped: 'Shipped',
  completed: 'Delivered',
  cancelled: 'Cancelled',
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default function TrackOrderPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [orderId, setOrderId] = useState(searchParams.get('orderId') || '');
  const [email, setEmail] = useState(location.state?.email || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const doTrack = async (oId, em) => {
    if (!oId.trim() || !em.trim()) {
      setError('Please enter both your Order ID and email.');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/orders/track`, {
        orderId: oId.trim(),
        email: em.trim(),
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to track order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTrack = (e) => {
    e.preventDefault();
    doTrack(orderId, email);
  };

  // Auto-track when arriving from checkout with both order id + email prefilled
  useEffect(() => {
    const prefillOrderId = searchParams.get('orderId');
    const prefillEmail = location.state?.email;
    if (prefillOrderId && prefillEmail) {
      doTrack(prefillOrderId, prefillEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isCancelled = result?.orderStatus === 'cancelled';
  const currentStepIndex = result
    ? STATUS_STEPS.findIndex((s) => s.key === result.orderStatus)
    : -1;

  return (
    <div className="min-h-[70vh] bg-slate-50" data-testid="track-order-page">
      {/* Hero */}
      <div className="bg-slate-900 text-white">
        <div className="container-custom py-12 lg:py-16">
          <div className="flex items-center gap-2 text-orange-400 mb-3">
            <Truck className="w-5 h-5" />
            <span className="text-sm font-semibold uppercase tracking-wide">Order Tracking</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3">Track Your Order</h1>
          <p className="text-slate-300 max-w-xl text-sm md:text-base">
            Enter your Order ID and the email used at checkout to see the latest status and shipping
            updates for your marine engine.
          </p>
        </div>
      </div>

      <div className="container-custom py-10">
        {/* Search Form */}
        <form
          onSubmit={handleTrack}
          className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 md:p-8 max-w-3xl mx-auto -mt-20 relative z-10"
          data-testid="track-order-form"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="track-order-id" className="text-slate-700">Order ID</Label>
              <Input
                id="track-order-id"
                placeholder="e.g. ORD-7E51F215"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                className="mt-1.5"
                data-testid="track-order-id-input"
              />
            </div>
            <div>
              <Label htmlFor="track-email" className="text-slate-700">Email</Label>
              <Input
                id="track-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
                data-testid="track-email-input"
              />
            </div>
          </div>

          {error && (
            <div
              className="flex items-center gap-2 mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2"
              data-testid="track-error"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="btn-primary w-full md:w-auto mt-5"
            disabled={loading}
            data-testid="track-submit-button"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Search className="w-4 h-4 mr-2" />
            )}
            Track Order
          </Button>
        </form>

        {/* Result */}
        {result && (
          <div className="max-w-3xl mx-auto mt-8 space-y-6" data-testid="track-result">
            {/* Order Summary */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Order ID</p>
                  <p className="font-mono text-lg font-bold text-slate-900" data-testid="result-order-id">
                    {result.orderId}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">{result.engineTitle}</p>
                </div>
                <Badge
                  className={`text-sm px-3 py-1 ${
                    isCancelled ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                  }`}
                  data-testid="result-status-badge"
                >
                  {statusLabels[result.orderStatus] || result.orderStatus}
                </Badge>
              </div>
              <div className="grid sm:grid-cols-3 gap-4 mt-5 pt-5 border-t border-slate-100 text-sm">
                <div>
                  <p className="text-slate-400">Customer</p>
                  <p className="font-medium text-slate-800">{result.customerName}</p>
                </div>
                <div>
                  <p className="text-slate-400">Destination</p>
                  <p className="font-medium text-slate-800">
                    {[result.shippingCity, result.shippingRegion, result.shippingCountry]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400">Order Placed</p>
                  <p className="font-medium text-slate-800">{formatDate(result.created_at)}</p>
                </div>
              </div>
            </div>

            {/* Progress Stepper */}
            {!isCancelled && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                <h3 className="font-semibold text-slate-900 mb-6">Order Progress</h3>
                <div className="flex items-center justify-between">
                  {STATUS_STEPS.map((step, idx) => {
                    const Icon = step.icon;
                    const reached = currentStepIndex >= idx && currentStepIndex !== -1;
                    const isCurrent = currentStepIndex === idx;
                    return (
                      <div key={step.key} className="flex-1 flex flex-col items-center relative">
                        {idx > 0 && (
                          <div
                            className={`absolute top-5 right-1/2 w-full h-0.5 ${
                              reached ? 'bg-orange-500' : 'bg-slate-200'
                            }`}
                          />
                        )}
                        <div
                          className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                            reached
                              ? 'bg-orange-500 text-white'
                              : 'bg-slate-100 text-slate-400'
                          } ${isCurrent ? 'ring-4 ring-orange-100' : ''}`}
                        >
                          <Icon className="w-5 h-5" />
                        </div>
                        <span
                          className={`mt-2 text-[10px] sm:text-xs text-center leading-tight ${
                            reached ? 'text-slate-800 font-medium' : 'text-slate-400'
                          }`}
                        >
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tracking Timeline */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="font-semibold text-slate-900 mb-5 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-orange-500" />
                Shipping Updates
              </h3>
              {result.trackingEvents && result.trackingEvents.length > 0 ? (
                <ol className="relative border-l-2 border-slate-100 ml-2" data-testid="tracking-timeline">
                  {[...result.trackingEvents].reverse().map((ev, idx) => (
                    <li key={ev.id || idx} className="mb-6 last:mb-0 ml-6">
                      <span
                        className={`absolute -left-[9px] flex items-center justify-center w-4 h-4 rounded-full ${
                          idx === 0 ? 'bg-orange-500' : 'bg-slate-300'
                        }`}
                      />
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-slate-900">{ev.location}</p>
                        {ev.status && (
                          <Badge variant="outline" className="text-[10px] capitalize">
                            {statusLabels[ev.status] || ev.status}
                          </Badge>
                        )}
                      </div>
                      {ev.note && <p className="text-sm text-slate-600 mt-1">{ev.note}</p>}
                      <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(ev.timestamp)}
                      </p>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Package className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">
                    No shipping updates yet. We'll post location updates here as your order moves.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
